// pages/AccessPrivileges.jsx
import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { getRequest, postRequest } from '../services/apiService';
import AddMenuModal from '../accessPrivileges/AddMenuModal';
import MenuItem from './MenuItemUser';

export default function UserAccess() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [showAddMainMenuModal, setShowAddMainMenuModal] = useState(false);
  const [showAddSubMenuModal, setShowAddSubMenuModal] = useState(false);
  const [currentParentId, setCurrentParentId] = useState(null);
  const [newMenuName, setNewMenuName] = useState('');
  const [newMenuUrl, setNewMenuUrl] = useState('');
  const [newMenuIcon, setNewMenuIcon] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionsList, setPermissionsList] = useState([]);

  // Fetch permissions list when component mounts
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setIsLoading(true);
        const permissionsData = await getRequest('access/privileges/');
        setPermissionsList(permissionsData);
        setIsLoading(false);
      } catch (error) {
        setError('Failed to fetch permissions');
        setIsLoading(false);
        console.error('Error fetching permissions:', error);
      }
    };

    fetchPermissions();
  }, []);

  // Fetch users when component mounts
  // Fetch users when component mounts
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const response = await getRequest('https://pms-api.ilizien-projects-cdf.in/api/auth/users/');
        
        console.log("API Response:", response); // Debug: Log full response
        console.log("Results Data:", response?.results?.data); // Debug: Check nested data
        
        const usersData = response?.results?.data || [];
        console.log("Users Data:", usersData); // Debug: Verify extracted data
        
        setUsers(usersData);
        setIsLoading(false);
      } catch (error) {
        setError('Failed to fetch users');
        setIsLoading(false);
        console.error('Error fetching users:', error);
      }
    };

  fetchUsers();
}, []);

  // Fetch menu items with privileges when user is selected
  useEffect(() => {
    const fetchMenusWithPrivileges = async () => {
      if (!selectedUser) {
        setMenuItems([]);
        return;
      }

      try {
        setIsLoading(true);
        // Note: You may need to update this endpoint to your actual user-specific privileges endpoint
        const menusData = await getRequest(`access/user-menu-mappings/?user_id=${selectedUser}`);
        
        // Transform API response to our component's format
        const transformedMenus = menusData.map(item => transformMenuFormat(item));
        setMenuItems(transformedMenus);
        setIsLoading(false);
      } catch (error) {
        setError('Failed to fetch menus with privileges');
        setIsLoading(false);
        console.error('Error fetching menus with privileges:', error);
      }
    };

    fetchMenusWithPrivileges();
  }, [selectedUser]);

  // Transform the API menu format to our component's format
  const transformMenuFormat = (item) => {
    // Convert privileges array to permissions object
    const permissions = {};
    if (item.privileges && item.privileges.length > 0) {
      item.privileges.forEach(priv => {
        permissions[priv.name.toLowerCase()] = priv.status;
      });
    }

    return {
      id: item.id,
      name: item.name,
      url: item.url,
      icon: item.icon,
      expanded: true,
      enabled: true,
      permissions: permissions,
      children: item.sub_menus ? item.sub_menus.map(subItem => 
        transformMenuFormat(subItem)
      ) : []
    };
  };

  // Toggle expansion of menu item
  const toggleExpand = (id, items = menuItems) => {
    return items.map(item => {
      if (item.id === id) {
        return { ...item, expanded: !item.expanded };
      } else if (item.children && item.children.length > 0) {
        return { ...item, children: toggleExpand(id, item.children) };
      }
      return item;
    });
  };

  // Toggle enabled state
  const toggleEnabled = (id, items = menuItems) => {
    return items.map(item => {
      if (item.id === id) {
        const newEnabledState = !item.enabled;
        return { 
          ...item, 
          enabled: newEnabledState,
          children: item.children.map(child => toggleEnabledRecursive(child, newEnabledState))
        };
      } else if (item.children && item.children.length > 0) {
        return { ...item, children: toggleEnabled(id, item.children) };
      }
      return item;
    });
  };

  // Helper function to recursively update enabled state for all children
  const toggleEnabledRecursive = (item, enabledState) => {
    return {
      ...item,
      enabled: enabledState,
      children: item.children.map(child => toggleEnabledRecursive(child, enabledState))
    };
  };

  // Update permission for a menu item
  const updatePermission = (id, permKey, value, items = menuItems, updateChildren = true, updateParents = false, parentChain = []) => {
    return items.map(item => {
      if (item.id === id) {
        const updatedItem = { 
          ...item, 
          permissions: { ...item.permissions, [permKey]: value } 
        };
        
        if (item.children && item.children.length > 0) {
          updatedItem.children = item.children.map(child => 
            updatePermissionRecursive(child, permKey, value)
          );
        }
        
        if (updateParents && parentChain.length > 0 && value) {
          parentChain.forEach(parent => {
            parent.permissions = { ...parent.permissions, [permKey]: true };
          });
        }
        
        return updatedItem;
      } else if (item.children && item.children.length > 0) {
        const newParentChain = updateParents ? [...parentChain, item] : parentChain;
        const updatedChildren = updatePermission(
          id, 
          permKey, 
          value, 
          item.children, 
          updateChildren, 
          updateParents, 
          newParentChain
        );
        
        return { ...item, children: updatedChildren };
      }
      return item;
    });
  };

  // Helper function to recursively update permissions for all children
  const updatePermissionRecursive = (item, permKey, value) => {
    return {
      ...item,
      permissions: { ...item.permissions, [permKey]: value },
      children: item.children.map(child => updatePermissionRecursive(child, permKey, value))
    };
  };

  // Add a new main menu
  const addMainMenu = async () => {
    if (!newMenuName.trim()) return;
    
    try {
      setIsLoading(true);
      
      const menuData = {
        name: newMenuName,
        url: newMenuUrl || "/",
        icon: newMenuIcon || "MessageSquare",
        status_db: 1
      };
      
      const response = await postRequest('access/menus/', menuData);
      
      const newMenu = transformMenuFormat({
        id: response.id,
        name: response.name,
        url: response.url,
        icon: response.icon,
        sub_menus: []
      });
      
      setMenuItems([...menuItems, newMenu]);
      setIsLoading(false);
      
      setNewMenuName('');
      setNewMenuUrl('');
      setNewMenuIcon('');
      setShowAddMainMenuModal(false);
      
    } catch (error) {
      setError('Failed to add menu');
      setIsLoading(false);
      console.error('Error adding menu:', error);
    }
  };

  // Add a new submenu
  const addSubMenu = async () => {
    if (!newMenuName.trim() || !currentParentId) return;
    
    try {
      setIsLoading(true);
      
      const subMenuData = {
        name: newMenuName,
        url: newMenuUrl || "/",
        icon: newMenuIcon || "icon",
        parent: currentParentId,
        status_db: 1
      };
      
      const response = await postRequest('access/menus/', subMenuData);
      
      const newSubMenu = transformMenuFormat({
        id: response.id,
        name: response.name,
        url: response.url,
        icon: response.icon,
        sub_menus: []
      });
      
      const updatedMenuItems = addSubmenuToParent(menuItems, currentParentId, newSubMenu);
      setMenuItems(updatedMenuItems);
      setIsLoading(false);
      
      setNewMenuName('');
      setNewMenuUrl('');
      setNewMenuIcon('');
      setShowAddSubMenuModal(false);
      
    } catch (error) {
      setError('Failed to add submenu');
      setIsLoading(false);
      console.error('Error adding submenu:', error);
    }
  };

  // Helper function to add submenu to parent in the UI
  const addSubmenuToParent = (items, parentId, newSubMenu) => {
    return items.map(item => {
      if (item.id === parentId) {
        return {
          ...item,
          expanded: true,
          children: [...item.children, newSubMenu]
        };
      } else if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: addSubmenuToParent(item.children, parentId, newSubMenu)
        };
      }
      return item;
    });
  };

  // Save permissions for selected user
  const savePermissions = async () => {
    if (!selectedUser) return;
    
    try {
      setIsLoading(true);
      
      const permissionsData = {
        user: { id: parseInt(selectedUser) },
        menu_access: transformMenuItemsForApi(menuItems)
      };
      
      await postRequest('access/user-menu-mappings/', permissionsData);
      
      setIsLoading(false);
      alert('Permissions saved successfully!');
      
    } catch (error) {
      setError('Failed to save permissions');
      setIsLoading(false);
      console.error('Error saving permissions:', error);
    }
  };

  // Helper function to transform menu items to API format
  const transformMenuItemsForApi = (items) => {
    return items.map(item => {
      const privileges = [];
      
      if (item.permissions) {
        for (const [key, value] of Object.entries(item.permissions)) {
          const permission = permissionsList.find(p => p.name.toLowerCase() === key);
          if (permission) {
            privileges.push({
              id: permission.id,
              name: permission.name,
              description: permission.description,
              status: value
            });
          }
        }
      }
      
      return {
        id: item.id,
        name: item.name,
        url: item.url,
        icon: item.icon,
        sub_menus: item.children ? transformMenuItemsForApi(item.children) : [],
        privileges: privileges
      };
    });
  };
   function getGridColsClass(length) {
  const maxCols = 12; // Optional: cap the max number of columns
  const colCount = Math.min(length, maxCols);
  return `grid-cols-${colCount}`;
}

  return (
    <div className="container mx-auto p-4 bg-white">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-blue-700">User Access Privileges</h1>
          <button 
            onClick={() => setShowAddMainMenuModal(true)}
            className="ml-4 p-1 text-blue-500 hover:bg-blue-50 rounded-full"
          >
            <Plus size={24} />
          </button>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <span className="mr-2">Select User</span>
            <select
  value={selectedUser}
  onChange={(e) => setSelectedUser(e.target.value)}
  className="border border-gray-300 rounded-md px-3 py-2"
  disabled={isLoading}
>
  <option value="">Select a user</option>
  {users.length > 0 ? (
    users.map((user) => (
      <option key={user.id} value={user.id}>
        {user.first_name} {user.last_name} ({user.loginname})
      </option>
    ))
  ) : (
    <option disabled>No users found</option>
  )}
</select>
          </div>
          
          <button 
            className="bg-blue-500 text-white px-6 py-2 rounded-md disabled:bg-blue-300"
            onClick={savePermissions}
            disabled={!selectedUser || isLoading}
          >
            {isLoading ? 'Processing...' : 'Save'}
          </button>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {selectedUser ? (
        isLoading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <div className="flex border-b border-gray-200 bg-gray-50 font-medium">
              <div className="w-60 p-3">Menu</div>
              <div className={`flex-1 grid text-center gap-4 ${getGridColsClass(permissionsList.length)}`}>
                {permissionsList.map((permission) => (
                  <div key={permission.id} className="p-3">{permission.name}</div>
                ))}
              </div>
              <div className="w-20 p-3 text-center">Active</div>
              {/* <div className="w-24 p-3 text-center">Actions</div> */}
            </div>
            
            <div>
              {menuItems.length > 0 ? (
                menuItems.map((item) => (
                  <MenuItem 
                    key={item.id} 
                    item={item} 
                    toggleExpand={(id) => setMenuItems(toggleExpand(id))}
                    updatePermission={(id, permKey, value, updateChildren, updateParents) => 
                      setMenuItems(updatePermission(id, permKey, value, menuItems, updateChildren, updateParents))
                    }
                    toggleEnabled={(id) => setMenuItems(toggleEnabled(id))}
                    setCurrentParentId={setCurrentParentId}
                    setShowAddSubMenuModal={setShowAddSubMenuModal}
                    permissionsList={permissionsList}
                  />
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">No menu items available</div>
              )}
            </div>
          </div>
        )
      ) : (
        <div className="text-center py-8 text-gray-500">
          Please select a user to manage access privileges
        </div>
      )}
      
      <AddMenuModal
        show={showAddMainMenuModal}
        onClose={() => {
          setShowAddMainMenuModal(false);
          setNewMenuName('');
          setNewMenuUrl('');
          setNewMenuIcon('');
        }}
        title="Add Main Menu"
        onSave={addMainMenu}
        isSubmenu={false}
        newMenuName={newMenuName}
        setNewMenuName={setNewMenuName}
        newMenuUrl={newMenuUrl}
        setNewMenuUrl={setNewMenuUrl}
        newMenuIcon={newMenuIcon}
        setNewMenuIcon={setNewMenuIcon}
      />
      
      <AddMenuModal
        show={showAddSubMenuModal}
        onClose={() => {
          setShowAddSubMenuModal(false);
          setNewMenuName('');
          setNewMenuUrl('');
          setNewMenuIcon('');
        }}
        title="Add Sub Menu"
        onSave={addSubMenu}
        isSubmenu={true}
        newMenuName={newMenuName}
        setNewMenuName={setNewMenuName}
        newMenuUrl={newMenuUrl}
        setNewMenuUrl={setNewMenuUrl}
        newMenuIcon={newMenuIcon}
        setNewMenuIcon={setNewMenuIcon}
      />
    </div>
  );
}